import { LinkCellRenderer } from "@/controls/ag-grid/components/ButtonCellRenderer";
import { HeatShiftReport } from "@/pages/shift-report/models/ShiftReportModels";
import {
	dxtToLocalServerTime,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { ColDef } from "ag-grid-community";
import { get } from "lodash-es";

type Props = {
	heats: HeatShiftReport[];
};

export const useTableData = ({ heats }: Props) => {
	const {
		state: { settings },
	} = useUTLSettingsContext();

	const MAX_TTT = Number(get(settings, "SHIFTREPORT_MAX_TTT", 180));
	const MAX_POWER_ON = Number(get(settings, "SHIFTREPORT_MAX_POWER_ON", 140));
	const MAX_DURATION = Number(get(settings, "SHIFTREPORT_MAX_DURATION", 120));
	const MAX_TAP_TEMP = Number(get(settings, "SHIFTREPORT_MAX_TAPTEMP", 3900));
	const MAX_TRIM_TEMP = Number(get(settings, "SHIFTREPORT_MAX_TRIMTEMP", 3300));
	const MIN_SUPER_HEAT = Number(get(settings, "SHIFTREPORT_MIN_SUPERHEAT", 30));

	const columnData: ColDef[] = [
		{
			headerName: "Heat",
			field: "Heat",
			minWidth: 65,
			flex: 1,
		},
		{
			headerName: "Grade",
			field: "Grade",
			minWidth: 98,
			cellClassRules: {
				"max-limit": (params) => {
					return params.data.Grade !== params.data.ProgrammedGrade;
				},
			},
			flex: 1,
		},
		{
			headerName: "Ladle",
			field: "Ladle",
			minWidth: 57,
			flex: 1,
		},
		{
			headerName: "EAF Tap",
			field: "EAFTap",
			minWidth: 73,
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data.EAFTap}`, "HH:mm"),
			cellRenderer: (params: any) => {
				if (params.data.HeatEAFDELink)
					return <LinkCellRenderer {...params} linkProperty="HeatEAFDELink" />;
				else return <span style={{ color: "black" }}>{params.value}</span>;
			},
			flex: 1,
		},
		{
			headerName: "TTT",
			field: "TTT",
			minWidth: 50,
			valueGetter: (params) =>
				params.data.TTT ? params.data.TTT.toString() : "",
			cellClassRules: {
				"max-limit": (params) => {
					return Number(params.value) > MAX_TTT; //3hs in minutes
				},
			},
			flex: 1,
		},
		{
			headerName: "Power On",
			field: "PowerOn",
			minWidth: 82,
			cellClassRules: {
				"max-limit": (params) => {
					return Number(params.value) > MAX_POWER_ON; // in minutes
				},
			},
			flex: 1,
		},
		{
			headerName: "Tap Temp",
			field: "TapTemp",
			minWidth: 82,
			cellClassRules: {
				"max-limit": (params) => {
					return Number(params.value) > MAX_TAP_TEMP;
				},
			},
			flex: 1,
		},
		{
			headerName: "Trim Temp",
			field: "TrimTemp",
			minWidth: 88,
			cellClassRules: {
				"max-limit": (params) => {
					return Number(params.value) > MAX_TRIM_TEMP;
				},
			},
			flex: 1,
		},
		{
			headerName: "Ladle Open",
			field: "LadleOpen",
			minWidth: 61,
			wrapHeaderText: true,
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data.LadleOpen}`, "HH:mm"),
			cellRenderer: (params: any) => {
				if (params.data.HeatCASDELink)
					return <LinkCellRenderer {...params} linkProperty="HeatCASDELink" />;
				else return <span style={{ color: "black" }}>{params.value}</span>;
			},
			flex: 1,
		},
		{
			headerName: "Ladle Close",
			field: "LadleClose",
			minWidth: 61,
			wrapHeaderText: true,
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data.LadleClose}`, "HH:mm"),
			flex: 1,
		},
		{
			headerName: "Duration",
			field: "Duration",
			minWidth: 76,
			valueGetter: (params) =>
				params.data.Duration ? params.data.Duration.toFixed(2) : "",
			cellClassRules: {
				"max-limit": (params) => {
					return Number(params.value) > MAX_DURATION; //in minutes
				},
			},
			flex: 1,
		},
		{
			headerName: "Section",
			field: "Sections",
			minWidth: 70,
			wrapText: true,
			flex: 1,
		},
		{
			headerName: "Length",
			field: "Length",
			minWidth: 66,
			flex: 1,
		},
		{
			headerName: "Seq",
			field: "Sequence",
			minWidth: 48,
			flex: 1,
		},
		{
			headerName: "Last SH",
			field: "LastSuperheat",
			minWidth: 71,
			cellClassRules: {
				"max-limit": (params) => {
					return params.value !== null && Number(params.value) < MIN_SUPER_HEAT;
				},
			},
			flex: 1,
		},
		{
			headerName: "Tons",
			field: "Tons",
			minWidth: 53,
			valueGetter: (params) =>
				params.data.Tons ? params.data.Tons.toFixed(1) : "",
			flex: 1,
		},
		{
			headerName: "Scrap",
			field: "Scrap",
			minWidth: 60,
			valueGetter: (params) =>
				params.data.Scrap ? params.data.Scrap.toFixed(1) : "",
			flex: 1,
		},
	];
	const defaultColDef: ColDef = {
		flex: 1,
		resizable: true,
	};

	return { columnData, defaultColDef };
};
